import { apiService, normalizeProduct } from '@/services/api'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { LabelAlreadyAttachedError, Product, ScanShape } from '@/types'

function isProductLike(value: unknown): value is Product {
    if (!value || typeof value !== 'object') {
        return false
    }

    const candidate = value as Record<string, unknown>
    return typeof candidate.id === 'string' && typeof candidate.name === 'string'
}

export const useRegisterStore = defineStore('register', () => {
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    const recognizedLabelId = ref<string | null>(null)
    const selectedProduct = ref<Product | null>(null)

    const registerLabel = async (imageFile: File, shape?: ScanShape) => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.registerLabel(imageFile, shape)
            if (response.success && response.data) {
                recognizedLabelId.value = response.data.uniq_label_id
                return { success: true, data: response.data }
            } else {
                error.value = response.error?.message || 'Label recognition failed'
                return { success: false, error: response.error }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Label recognition failed'
            return { success: false, error: { code: -1, message: error.value } }
        } finally {
            isLoading.value = false
        }
    }

    const attachLabel = async (
        productId: string,
        uniqLabelId: string
    ): Promise<
        | { success: true; product: Product }
        | {
              success: false
              error: { code: number; message: string }
              alreadyAttached?: LabelAlreadyAttachedError
          }
    > => {
        try {
            isLoading.value = true
            error.value = null

            const response = await apiService.attachLabelToProduct(productId, uniqLabelId)
            if (response.success && response.data) {
                const product = normalizeProduct(response.data.product)
                return { success: true, product }
            } else {
                error.value = response.error?.message || 'Failed to attach label'

                // Check if this is a 409 conflict (already attached)
                if (response.error?.code === 409) {
                    const errRaw = response.error as Record<string, unknown>
                    const alreadyAttached: LabelAlreadyAttachedError = {
                        title: 'Conflict',
                        detail: response.error.message,
                        existing_product_id: String(errRaw.existing_product_id ?? ''),
                        existing_product: isProductLike(errRaw.existing_product)
                            ? normalizeProduct(errRaw.existing_product)
                            : null,
                    }
                    return { success: false, error: response.error, alreadyAttached }
                }

                return {
                    success: false,
                    error: response.error ?? { code: -1, message: error.value },
                }
            }
        } catch (err: unknown) {
            error.value = (err as Error)?.message || 'Failed to attach label'
            return { success: false, error: { code: -1, message: error.value } }
        } finally {
            isLoading.value = false
        }
    }

    const reset = () => {
        recognizedLabelId.value = null
        selectedProduct.value = null
        error.value = null
    }

    return {
        isLoading,
        error,
        recognizedLabelId,
        selectedProduct,
        registerLabel,
        attachLabel,
        reset,
    }
})
